/*
 * Figma `77:2`–`81:228` — LP-01…LP-09, the Landing Page editor. The design freeze calls this the
 * gold standard, and the thing it is a standard *of* is the three-region editor: a rail that says
 * which parts still need work, one section open at a time, and a live preview of the page a buyer
 * will actually read.
 *
 * The shipped panel stacked all six sections in one column. That is why the rail matters rather
 * than being decoration: an editor split into six panels hides five of them, so the rail carries a
 * completeness summary and the only way to find the unfinished section is not to open all six.
 *
 * `problems` is the other substantive change. The old panel sent the object and let the server's
 * 400 arrive as one opaque sentence; `80:286` shows the save refused with its reasons listed and
 * each one jumping to the field. The rules live in `useLandingContent` and are the server's own —
 * a client that refuses a save the server would have accepted invents a rule nobody wrote.
 */
import { useState } from 'react';
import type { ReactNode } from 'react';
import {
  Alert, Button, Card, EditorTemplate, SaveBar, SectionNav, SectionPanel, StatusBadge,
  TextField, Textarea, Toggle,
} from '..';
import type { SaveState, SectionNavItem } from '..';
import type { Destination } from '../destinations';
import { ImageSlot } from '../../components/ImageSlot';
import type { ContentDto } from '../../api';
import type { LandingProblem, LandingSectionKey, LandingState } from './useLandingContent';
import { LIMITS, useLandingContent } from './useLandingContent';

type Phase = 'pre' | 'post';

function navItems(content: ContentDto, problems: readonly LandingProblem[]): SectionNavItem[] {
  const trouble = (section: LandingSectionKey) => problems.some((p) => p.section === section);
  const item = (
    key: LandingSectionKey, label: string, done: boolean, status: string,
  ): SectionNavItem => ({
    key,
    label,
    status,
    tone: trouble(key) ? 'attention' : done ? 'complete' : 'empty',
  });

  return [
    item('basics', 'ข้อมูลหลัก', !!content.name.trim() && !!content.tagline.trim(), content.name.trim() ? 'ครบแล้ว' : 'ยังไม่ได้กรอก'),
    item('hero', 'รูปภาพ Hero', !!content.heroImageKey, content.heroImageKey ? (content.heroImagePortraitKey ? 'ครบทั้งสองขนาด' : 'มีเฉพาะจอกว้าง') : 'ยังไม่มีรูป'),
    item('tickets', 'ข้อความขายบัตร', !!content.ticketsTagline.trim(), content.ticketsTaglineVisible ? (content.ticketsTagline.trim() ? 'แสดงอยู่' : 'เปิดไว้แต่ยังไม่มีข้อความ') : 'ซ่อนอยู่'),
    item('facts', 'Event facts', content.facts.length > 0, `${content.facts.length} รายการ`),
    item('lineup', 'Lineup', content.lineup.length > 0, `${content.lineup.length} ศิลปิน`),
    item('sections', 'FAQ & Sponsors', content.sections.length > 0, `${content.sections.reduce((n, s) => n + s.items.length, 0)} รายการ`),
  ];
}

/** Figma `79:194` — Landing Editor / Repeater Row. One entry of a list the organizer reorders. */
function RepeaterRow({ title, onUp, onDown, onRemove, children }: {
  title: string;
  onUp?: () => void;
  onDown?: () => void;
  onRemove: () => void;
  children: ReactNode;
}) {
  return (
    <div className="jt-repeater-row" role="group" aria-label={title}>
      <div className="jt-repeater-head">
        <span className="jt-repeater-title">{title}</span>
        <div className="jt-inline-actions">
          {onUp && <Button size="sm" variant="ghost" aria-label={`เลื่อน ${title} ขึ้น`} onClick={onUp}>↑</Button>}
          {onDown && <Button size="sm" variant="ghost" aria-label={`เลื่อน ${title} ลง`} onClick={onDown}>↓</Button>}
          <Button size="sm" variant="ghost" aria-label={`ลบ ${title}`} onClick={onRemove}>ลบ</Button>
        </div>
      </div>
      <div className="jt-repeater-body">{children}</div>
    </div>
  );
}

/** Figma `77:96` — the live preview rail. A sketch of the public page, not an iframe of it. */
function LandingPreview({ content, phase, eventSlug }: {
  content: ContentDto;
  phase: Phase;
  eventSlug?: string;
}) {
  const cs = content.comingSoon;
  return (
    <div className="jt-lp-preview">
      <div className="jt-lp-chrome">
        <span className="jt-lp-dots" aria-hidden="true">● ● ●</span>
        <span className="jt-lp-url">{eventSlug ? `jamtick.live/${eventSlug}` : 'jamtick.live'}</span>
      </div>
      <div className="jt-lp-hero">
        <p className="jt-lp-eyebrow">{(phase === 'pre' ? cs?.eyebrow : content.name) || '—'}</p>
        <p className="jt-lp-title">{(phase === 'pre' ? cs?.headline : content.tagline) || 'ยังไม่ได้กรอก'}</p>
        {phase === 'post' && content.earlyBirdNote && <p className="jt-lp-note">{content.earlyBirdNote}</p>}
        {phase === 'pre' && cs?.message && <p className="jt-lp-note">{cs.message}</p>}
        <span className="jt-lp-cta">{phase === 'pre' ? cs?.ctaLabel || 'รับข่าวเปิดขาย' : 'ซื้อบัตร'}</span>
      </div>
      {phase === 'post' && (
        <div className="jt-lp-body">
          {content.facts.length > 0 && (
            <dl className="jt-lp-facts">
              {content.facts.slice(0, 4).map((fact, i) => (
                <div key={`${fact.k}-${i}`}><dt>{fact.k || '—'}</dt><dd>{fact.v || '—'}</dd></div>
              ))}
            </dl>
          )}
          <p className="jt-lp-section-label">LINEUP</p>
          {content.lineup.length === 0
            ? <p className="jt-lp-empty">ยังไม่มีรายชื่อศิลปิน</p>
            : content.lineup.slice(0, 4).map((artist, i) => (
              <p key={`${artist.name}-${i}`} className="jt-lp-line">{artist.name || '—'}{artist.time ? ` · ${artist.time}` : ''}</p>
            ))}
        </div>
      )}
    </div>
  );
}

export function LandingScreen({ destination, state, permitted, role, token, eventSlug }: {
  destination: Destination;
  state: LandingState;
  permitted: boolean;
  role?: string | null;
  /** Needed only by `ImageSlot`, which uploads straight to the event's own asset endpoint. */
  token: string;
  eventSlug?: string;
}) {
  const {
    content, loading, loadError, saving, justSaved, saveError, dirty, problems, blocking,
    set, setComingSoon, moveArtist, moveFact, addSection, save, discard,
  } = state;
  const [phase, setPhase] = useState<Phase>('post');
  const [section, setSection] = useState<LandingSectionKey>('basics');

  const saveState: SaveState =
    saveError ? 'error' : saving ? 'saving' : dirty ? 'dirty' : justSaved ? 'saved' : 'clean';

  const editor = (c: ContentDto): ReactNode => {
    switch (section) {
      case 'basics':
        return (
          <Card title="ข้อมูลหลัก" sub="ข้อมูลชุดนี้แสดงใน Hero และข้อมูลติดต่อท้ายหน้า">
            <TextField label="ชื่องาน" requiredMark value={c.name} onChange={(e) => set('name', e.target.value)} />
            <Textarea label="Tagline" requiredMark limit={LIMITS.tagline} value={c.tagline} onChange={(e) => set('tagline', e.target.value)}
              hint="ข้อความสั้น อ่านง่าย และไม่ซ้ำกับรายละเอียดด้านล่าง จะช่วยให้ผู้ซื้อเข้าใจงานภายใน 5 วินาที" />
            <Textarea label="ข้อความ Early Bird" limit={LIMITS.earlyBirdNote} value={c.earlyBirdNote} onChange={(e) => set('earlyBirdNote', e.target.value)} />
            <div className="jt-field-grid">
              <TextField label="อีเมลติดต่อ" type="email" value={c.contactEmail} onChange={(e) => set('contactEmail', e.target.value)} />
              <TextField label="ชื่อบริษัท (footer)" value={c.company} onChange={(e) => set('company', e.target.value)} />
            </div>
          </Card>
        );
      case 'hero':
        return (
          <Card title="รูปภาพ Hero" sub="เตรียมรูปสำหรับแต่ละ viewport แยกกัน เพื่อควบคุม crop และข้อความที่อ่านได้">
            <ImageSlot
              token={token} eventSlug={eventSlug}
              imageKey={c.heroImageKey} onChange={(k) => set('heroImageKey', k)}
              alt="hero" label="รูป Hero — จอกว้าง (Desktop)"
              placeholder="รูป Hero แนวนอน — ลากไฟล์มาวาง"
              enableCrop cropAspect={21 / 9}
            />
            <ImageSlot
              token={token} eventSlug={eventSlug}
              imageKey={c.heroImagePortraitKey} onChange={(k) => set('heroImagePortraitKey', k)}
              alt="hero portrait" aspect="4 / 5" label="รูป Hero — มือถือ (ไม่ใส่ก็ได้)"
              placeholder="รูป Hero แนวตั้ง — ลากไฟล์มาวาง"
              enableCrop cropAspect={4 / 5}
            />
            <Alert tone="info" title="Crop rules">
              จอกว้าง 21:9 · มือถือ 4:5 · เนื้อหาสำคัญให้อยู่กลางภาพ · การเปลี่ยนรูปยังไม่เผยแพร่จนกว่าจะกดบันทึก
            </Alert>
          </Card>
        );
      case 'tickets':
        return (
          <Card title="ข้อความขายบัตร" sub="ป้ายข้อความเล็กข้างหัวข้อ TICKETS บนหน้า Landing Page">
            <Toggle
              label="แสดงข้อความนี้"
              checked={c.ticketsTaglineVisible}
              onChange={(e) => set('ticketsTaglineVisible', e.target.checked)}
              consequence="ปิดไว้ได้โดยไม่ต้องลบข้อความ — ข้อความจะยังถูกเก็บไว้"
            />
            <Textarea
              label="Tagline หมวดตั๋ว"
              limit={LIMITS.ticketsTagline}
              value={c.ticketsTagline}
              onChange={(e) => set('ticketsTagline', e.target.value)}
              disabled={!c.ticketsTaglineVisible}
            />
          </Card>
        );
      case 'facts':
        return (
          <Card
            title="Event facts"
            sub={`แถบข้อมูลใต้ Hero · สูงสุด ${LIMITS.facts} รายการ`}
            action={
              <Button
                size="sm"
                disabled={c.facts.length >= LIMITS.facts}
                onClick={() => set('facts', [...c.facts, { k: '', v: '' }])}
              >
                ＋ เพิ่มรายการ
              </Button>
            }
          >
            {c.facts.length === 0 && <p className="jt-field-hint">ยังไม่มีรายการ — แถบนี้จะไม่แสดงบนหน้าเว็บ</p>}
            {c.facts.map((fact, i) => (
              <RepeaterRow
                key={i}
                title={fact.k || `รายการที่ ${i + 1}`}
                onUp={i > 0 ? () => moveFact(i, i - 1) : undefined}
                onDown={i < c.facts.length - 1 ? () => moveFact(i, i + 1) : undefined}
                onRemove={() => set('facts', c.facts.filter((_, j) => j !== i))}
              >
                <div className="jt-field-grid">
                  <TextField label="หัวข้อ" value={fact.k}
                    onChange={(e) => set('facts', c.facts.map((f, j) => (j === i ? { ...f, k: e.target.value } : f)))} />
                  <TextField label="ข้อมูล" value={fact.v}
                    onChange={(e) => set('facts', c.facts.map((f, j) => (j === i ? { ...f, v: e.target.value } : f)))} />
                </div>
              </RepeaterRow>
            ))}
          </Card>
        );
      case 'lineup':
        return (
          <Card
            title="Lineup"
            sub={`รายชื่อศิลปิน · สูงสุด ${LIMITS.lineup} รายชื่อ`}
            action={
              <Button
                size="sm"
                disabled={c.lineup.length >= LIMITS.lineup}
                onClick={() => set('lineup', [...c.lineup, { name: '', time: '', photoKey: null }])}
              >
                ＋ เพิ่มศิลปิน
              </Button>
            }
          >
            <TextField
              label="จำนวนวันที่แสดง"
              type="number" min={1} max={30}
              value={c.lineupDays}
              onChange={(e) => set('lineupDays', Math.max(1, Math.min(30, Number(e.target.value) || 1)))}
            />
            {c.lineup.length === 0 && <p className="jt-field-hint">ยังไม่มีรายชื่อ — ส่วน LINEUP จะไม่แสดงบนหน้าเว็บ</p>}
            {c.lineup.map((artist, i) => (
              <RepeaterRow
                key={i}
                title={artist.name || `ศิลปินที่ ${i + 1}`}
                onUp={i > 0 ? () => moveArtist(i, -1) : undefined}
                onDown={i < c.lineup.length - 1 ? () => moveArtist(i, 1) : undefined}
                onRemove={() => set('lineup', c.lineup.filter((_, j) => j !== i))}
              >
                <div className="jt-field-grid">
                  <TextField label="ชื่อศิลปิน" value={artist.name}
                    onChange={(e) => set('lineup', c.lineup.map((a, j) => (j === i ? { ...a, name: e.target.value } : a)))} />
                  <TextField label="เวลา" value={artist.time} placeholder="20:30"
                    onChange={(e) => set('lineup', c.lineup.map((a, j) => (j === i ? { ...a, time: e.target.value } : a)))} />
                </div>
                <ImageSlot
                  token={token} eventSlug={eventSlug}
                  imageKey={artist.photoKey} aspect="1 / 1" size={96}
                  onChange={(k) => set('lineup', c.lineup.map((a, j) => (j === i ? { ...a, photoKey: k } : a)))}
                  alt={artist.name || 'artist'} label="รูปศิลปิน (ไม่ใส่ก็ได้)"
                  placeholder="ลากรูปมาวาง"
                />
              </RepeaterRow>
            ))}
          </Card>
        );
      case 'sections':
        return (
          <Card
            title="FAQ & Sponsors"
            sub="คำถามช่วยลดการติดต่อซ้ำ ส่วน Sponsor แสดงตามลำดับที่กำหนด"
            action={
              <div className="jt-inline-actions">
                <Button size="sm" disabled={c.sections.length >= LIMITS.sections} onClick={() => addSection('faq')}>＋ FAQ</Button>
                <Button size="sm" disabled={c.sections.length >= LIMITS.sections} onClick={() => addSection('sponsors')}>＋ สปอนเซอร์</Button>
              </div>
            }
          >
            {c.sections.length === 0 && <p className="jt-field-hint">ยังไม่มี Section เพิ่มเติม</p>}
            {c.sections.map((sec, i) => (
              <RepeaterRow
                key={sec.id}
                title={`${sec.title} · ${sec.type === 'faq' ? 'FAQ' : 'สปอนเซอร์'}`}
                onRemove={() => set('sections', c.sections.filter((_, j) => j !== i))}
              >
                <TextField label="หัวข้อ Section" value={sec.title}
                  onChange={(e) => set('sections', c.sections.map((s, j) => (j === i ? { ...s, title: e.target.value } : s)))} />
                {sec.type === 'faq' ? (
                  <>
                    {sec.items.map((item, k) => (
                      <div key={k} className="jt-field-grid">
                        <TextField label={`คำถามที่ ${k + 1}`} value={item.q}
                          onChange={(e) => set('sections', c.sections.map((s, j) => (j === i && s.type === 'faq'
                            ? { ...s, items: s.items.map((it, m) => (m === k ? { ...it, q: e.target.value } : it)) } : s)))} />
                        <TextField label="คำตอบ" value={item.a}
                          onChange={(e) => set('sections', c.sections.map((s, j) => (j === i && s.type === 'faq'
                            ? { ...s, items: s.items.map((it, m) => (m === k ? { ...it, a: e.target.value } : it)) } : s)))} />
                      </div>
                    ))}
                    <Button size="sm" variant="ghost"
                      onClick={() => set('sections', c.sections.map((s, j) => (j === i && s.type === 'faq'
                        ? { ...s, items: [...s.items, { q: '', a: '' }] } : s)))}>
                      ＋ เพิ่มคำถาม
                    </Button>
                  </>
                ) : (
                  <>
                    {sec.items.map((item, k) => (
                      <TextField key={k} label={`สปอนเซอร์ที่ ${k + 1}`} value={item.name}
                        onChange={(e) => set('sections', c.sections.map((s, j) => (j === i && s.type === 'sponsors'
                          ? { ...s, items: s.items.map((it, m) => (m === k ? { ...it, name: e.target.value } : it)) } : s)))} />
                    ))}
                    <Button size="sm" variant="ghost"
                      onClick={() => set('sections', c.sections.map((s, j) => (j === i && s.type === 'sponsors'
                        ? { ...s, items: [...s.items, { name: '', logoKey: null, url: '' }] } : s)))}>
                      ＋ เพิ่มสปอนเซอร์
                    </Button>
                  </>
                )}
              </RepeaterRow>
            ))}
          </Card>
        );
    }
  };

  return (
    <EditorTemplate
      gate={{ destination, loading, error: loadError || null, permitted, role }}
      sections={content && (
        <>
          {/*
            * Two jobs, and an event is only ever doing one of them: before the announcement the page
            * is a placeholder, and after it, the sales page every customer reads. Both halves edit
            * one object and are saved by the one bar below, so switching never loses an edit.
            */}
          <div className="jt-phase-tabs" role="tablist" aria-label="ช่วงของหน้า Landing">
            {([['pre', 'ก่อนประกาศ'], ['post', 'หลังประกาศ']] as const).map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={phase === key}
                className="jt-phase-tab"
                onClick={() => setPhase(key)}
              >
                {label}
              </button>
            ))}
          </div>

          {problems.length > 0 && (
            <Alert
              tone={blocking.length > 0 ? 'danger' : 'warning'}
              title={blocking.length > 0
                ? `บันทึกไม่ได้ — พบ ${blocking.length} จุดที่ต้องแก้ไข`
                : `ตรวจพบ ${problems.length} จุดที่ควรดู`}
            >
              <ul className="jt-consequences">
                {problems.map((problem, i) => (
                  <li key={`${problem.field}-${i}`}>
                    <button
                      type="button"
                      className="jt-problem-link"
                      onClick={() => { setPhase('post'); setSection(problem.section); }}
                    >
                      {problem.message}
                    </button>
                    {problem.kind === 'warning' && <StatusBadge tone="neutral">ไม่บล็อกการบันทึก</StatusBadge>}
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          {phase === 'post' ? (
            <div className="jt-landing-workspace">
              <SectionNav
                items={navItems(content, problems)}
                active={section}
                onSelect={(key) => setSection(key as LandingSectionKey)}
                label="เนื้อหาหน้าเว็บ"
                panelId="jt-landing-editor"
              />
              <SectionPanel id="jt-landing-editor" activeKey={section}>
                {editor(content)}
              </SectionPanel>
            </div>
          ) : (
            <Card title="หน้า Coming Soon" sub="ผู้ซื้อจะเห็นหน้านี้ก่อนเปิดขายจริง ใช้ข้อความสั้นและ CTA เพียงหนึ่งเป้าหมาย">
              <TextField label="Eyebrow" value={content.comingSoon?.eyebrow ?? ''} onChange={(e) => setComingSoon('eyebrow', e.target.value)} />
              <TextField label="Headline" value={content.comingSoon?.headline ?? ''} onChange={(e) => setComingSoon('headline', e.target.value)} />
              <Textarea label="ข้อความเพิ่มเติม" limit={LIMITS.comingSoonMessage}
                value={content.comingSoon?.message ?? ''} onChange={(e) => setComingSoon('message', e.target.value)} />
              <div className="jt-field-grid">
                <TextField label="ข้อความบนปุ่ม" value={content.comingSoon?.ctaLabel ?? ''} onChange={(e) => setComingSoon('ctaLabel', e.target.value)} />
                <TextField label="URL ปลายทาง" value={content.comingSoon?.ctaUrl ?? ''} placeholder="https://line.me/R/ti/p/@jamtick"
                  onChange={(e) => setComingSoon('ctaUrl', e.target.value)}
                  hint="ต้องขึ้นต้นด้วย http:// หรือ https:// เท่านั้น" />
              </div>
              <Toggle
                label="แสดงรูป Hero บน Coming Soon"
                checked={content.comingSoon?.showHero ?? true}
                onChange={(e) => setComingSoon('showHero', e.target.checked)}
                consequence="ปิดไว้ถ้ายังไม่อยากเปิดเผยภาพงาน"
              />
            </Card>
          )}
        </>
      )}
      preview={content && (
        <Card title="Live preview" sub={phase === 'pre' ? 'หน้าก่อนประกาศ' : 'หน้าขายบัตร'}>
          <LandingPreview content={content} phase={phase} eventSlug={eventSlug} />
        </Card>
      )}
      stickyActions={content && (
        <SaveBar
          state={saveState}
          error={saveError || null}
          blockedReason={blocking.length > 0 ? `แก้ ${blocking.length} จุดที่ระบุไว้ด้านบนก่อนจึงจะบันทึกได้` : null}
          onSave={() => void save()}
          onDiscard={dirty ? discard : undefined}
          saveLabel="บันทึกทั้งหมด"
          consequence="บันทึกแล้วหน้าเว็บสาธารณะจะเปลี่ยนทันที"
        />
      )}
    />
  );
}

export function LandingDestination({ token, eventSlug, destination, permitted, role }: {
  token: string;
  eventSlug?: string;
  destination: Destination;
  permitted: boolean;
  role?: string | null;
}) {
  const state = useLandingContent(token, eventSlug);
  return (
    <LandingScreen
      destination={destination}
      state={state}
      permitted={permitted}
      role={role}
      token={token}
      eventSlug={eventSlug}
    />
  );
}
